// src/components/HowItWorks.tsx - "Getting started" (route /how-to, the header's Help link)

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Skeleton,
  Typography,
} from '@mui/material';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import { useAuth } from '../context/AuthContext';
import { brand } from '../theme';
import { isEditorOnlyRole } from '../utils/editorAccount';
import PageContainer from './ui/PageContainer';
import PageHeader from './ui/PageHeader';
import Section from './ui/Section';
import {
  CREATOR_STEPS,
  EDITOR_FAQS,
  EDITOR_HOME_LINK,
  EDITOR_STEPS,
  FAQS,
  FaqEntry,
  GettingStartedStep,
  SUPPORT_EMAIL,
} from './GettingStarted/gettingStartedContent';

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <Box
    aria-hidden="true"
    sx={{
      width: 28,
      height: 28,
      flexShrink: 0,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      fontWeight: 700,
      fontSize: '0.8125rem',
      fontVariantNumeric: 'tabular-nums',
      color: brand.slate,
      border: `1px solid ${brand.borderStrong}`,
      backgroundColor: 'grey.100',
    }}
  >
    {n}
  </Box>
);

const StepList: React.FC<{ steps: GettingStartedStep[] }> = ({ steps }) => (
  <Box component="ol" aria-label="Steps" sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {steps.map((step, index) => (
      <Box
        component="li"
        key={step.title}
        data-testid="getting-started-step"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          px: 2.5,
          py: 2,
          borderTop: index === 0 ? 0 : 1,
          borderColor: 'divider',
        }}
      >
        <StepNumber n={index + 1} />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 0.75, sm: 2 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography component="h3" sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.5, mt: '2px' }}>
              {step.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {step.body}
            </Typography>
          </Box>
          {step.link && (
            <Button
              component={RouterLink}
              to={step.link.to}
              size="small"
              endIcon={<ArrowForwardOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', ml: { xs: -1.5, sm: 0 } }}
            >
              {step.link.label}
            </Button>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

const FaqSection: React.FC<{ faqs: FaqEntry[] }> = ({ faqs }) => (
  <Section title="Frequently asked questions" disablePadding>
    {faqs.map((faq, index) => {
      const id = `faq-${index}`;
      return (
        <Accordion
          key={faq.question}
          disableGutters
          elevation={0}
          square
          sx={{
            backgroundColor: 'transparent',
            borderTop: index === 0 ? 0 : 1,
            borderColor: 'divider',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary
            id={`${id}-header`}
            aria-controls={`${id}-content`}
            expandIcon={<ExpandMoreOutlinedIcon />}
            sx={{
              px: 2.5,
              minHeight: 56,
              '&.Mui-focusVisible': { backgroundColor: 'grey.100' },
            }}
          >
            <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {faq.question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      );
    })}
  </Section>
);

const HowItWorks: React.FC = () => {
  const { user, roleLoading } = useAuth();
  const editorOnly = isEditorOnlyRole(user?.role);

  return (
    <PageContainer width="narrow">
      <PageHeader
        title="Getting started"
        subtitle={
          editorOnly
            ? 'How editing a project works on PithPlay, from the brief to the cut you deliver.'
            : 'Six steps from raw footage to a finished edit, each linked to where you do it.'
        }
        action={
          !roleLoading && editorOnly ? (
            <Button component={RouterLink} to={EDITOR_HOME_LINK.to} variant="contained">
              {EDITOR_HOME_LINK.label}
            </Button>
          ) : undefined
        }
      />

      <Box sx={{ display: 'grid', gap: 3 }}>
        <Section title="Steps" disablePadding>
          {roleLoading ? (
            <Box role="status" aria-label="Loading steps" sx={{ px: 2.5, py: 2 }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, py: 1 }}>
                  <Skeleton variant="circular" width={28} height={28} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton width="40%" />
                    <Skeleton width="85%" />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <StepList steps={editorOnly ? EDITOR_STEPS : CREATOR_STEPS} />
          )}
        </Section>

        {!roleLoading && <FaqSection faqs={editorOnly ? EDITOR_FAQS : FAQS} />}

        <Section title="Contact support">
          <Typography variant="body2" color="text.secondary">
            {editorOnly ? 'Questions about your account or a project?' : 'Questions about your account, a project or billing?'}{' '}
            Email{' '}
            <Link href={`mailto:${SUPPORT_EMAIL}`} sx={{ fontWeight: 600 }}>
              {SUPPORT_EMAIL}
            </Link>{' '}
            and we&apos;ll get back to you.
          </Typography>
        </Section>
      </Box>
    </PageContainer>
  );
};

export default HowItWorks;
